import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../shared/ui/Button.js';
import { Logo } from '../../shared/ui/Logo.js';
import { ApiError, publicApi } from '../../shared/api/client.js';
import { passwordProblem, PASSWORD_RULE } from './password-messages.js';
import './login.css';

export type Purpose = 'activation' | 'password_reset';

const COPY: Readonly<Record<Purpose, { title: string; sub: string; path: string; done: string }>> = {
  activation: {
    title: 'Activer votre compte',
    sub: 'Choisissez le mot de passe de votre compte Ederest.',
    path: '/auth/activation',
    done: 'Votre compte est activé. Vous pouvez vous connecter.',
  },
  password_reset: {
    title: 'Nouveau mot de passe',
    sub: 'Choisissez un nouveau mot de passe. Vos autres sessions seront fermées.',
    path: '/auth/password-reset',
    done: 'Votre mot de passe est changé. Vous pouvez vous connecter.',
  },
};

/** The token travels in the fragment (`#token=`), which no server and no log ever receives. */
function tokenFromLocation(): string {
  return new URLSearchParams(window.location.hash.slice(1)).get('token') ?? '';
}

/** Landing page of an activation or reset link (functional §9.2): single use, valid 24 hours. */
export function SetPasswordPage({ purpose }: { purpose: Purpose }) {
  const copy = COPY[purpose];
  const [token] = useState(tokenFromLocation);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (password !== confirmation) {
      setError('Les deux saisies ne sont pas identiques.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await publicApi(copy.path, { token, password });
      setDone(true);
      window.history.replaceState(null, '', window.location.pathname);
    } catch (cause) {
      setError(
        cause instanceof ApiError && cause.code === 'ONE_TIME_TOKEN_INVALID'
          ? "Ce lien n'est plus valable : il a déjà servi ou ses 24 heures sont écoulées. Demandez-en un nouveau à l'administration centrale."
          : passwordProblem(cause),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login">
      <div className="login__card">
        <div className="login__brand">
          <Logo suffix="Analytics" />
        </div>
        <h1 className="login__title">{copy.title}</h1>
        <p className="login__sub">{copy.sub}</p>

        {done ? (
          <>
            <p className="login__success" role="status">
              {copy.done}
            </p>
            <Link to="/" className="btn btn--primary btn--md login__submit">
              Aller à la connexion
            </Link>
          </>
        ) : !token ? (
          <p className="login__error" role="alert">
            Ce lien est incomplet. Ouvrez-le tel qu'il vous a été transmis.
          </p>
        ) : (
          <form onSubmit={submit}>
            <label className="login__field">
              <span>Mot de passe</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="new-password"
              />
            </label>
            <label className="login__field">
              <span>Confirmation</span>
              <input
                type="password"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                required
                autoComplete="new-password"
              />
            </label>
            <p className="login__hint login__hint--rule">{PASSWORD_RULE}</p>
            {error && (
              <p className="login__error" role="alert">
                {error}
              </p>
            )}
            <Button variant="primary" size="md" type="submit" disabled={busy} className="login__submit">
              {busy ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
